import React, { useState, useEffect, useRef } from 'react';
import { Typography } from '@fiverr-private/typography';
import { useBreakpoint } from '@fiverr-private/hooks';
import { placeholderClass } from './styles.ve.css';

interface SearchAnimatedPlaceholderProps {
  placeholders: string[];
  isTouched: boolean;
}

const SearchAnimatedPlaceholder: React.FC<SearchAnimatedPlaceholderProps> = ({ placeholders, isTouched }) => {
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const breakpoint = useBreakpoint();

  useEffect(() => {
    setCurrentPlaceholderIndex(0);

    if (isTouched) {
      return;
    }

    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const getAnimationOffset = () => {
      if (breakpoint === 'md' || breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === 'xxl') {
        return '40px';
      }
      if (breakpoint === 'sm') {
        return '35px';
      }
      return '30px';
    };

    const applyStyles = (styles: Partial<CSSStyleDeclaration>) => {
      if (placeholderRef.current) {
        Object.assign(placeholderRef.current.style, styles);
      }
    };

    const animateTransition = async () => {
      const offset = getAnimationOffset();

      applyStyles({
        transform: `translateY(calc(-50% - ${offset}))`,
        opacity: '0',
      });

      await delay(500);

      setCurrentPlaceholderIndex((prev) => (prev + 1) % placeholders.length);

      applyStyles({
        transition: 'none',
        transform: `translateY(calc(-50% + ${offset}))`,
        opacity: '0',
      });

      await delay(50);

      applyStyles({
        transition: 'all 0.5s ease-in-out',
        transform: 'translateY(-50%)',
        opacity: '1',
      });
    };

    const interval = setInterval(animateTransition, 2550);
    return () => clearInterval(interval);
  }, [placeholders.length, isTouched, breakpoint]);

  return (
    <Typography
      ref={placeholderRef}
      className={placeholderClass}
      position="absolute"
      left="32px"
      top="50%"
      pointerEvents="none"
      color="grey_900"
      transform="translateY(-50%)"
      opacity="1"
      transition="all 0.5s ease-in-out"
    >
      {placeholders[currentPlaceholderIndex]}
    </Typography>
  );
};

export default SearchAnimatedPlaceholder;
